/** @format */

import React, { FC, useState, useRef, useEffect } from 'react'
import {
  Row,
  Col,
  Dialog,
  Tooltip,
  Button,
  Select,
  Option,
  Message,
  Card,
  _$
} from '@king-design/react'
import { GetCostOptInstances, QueryCheckRules, CreateCheckRules } from './GeneralApi'
import TablePage from 'src/components/TablePage'
import CoseHeaders from './GeneralCostHeader'
import RuleContent from './GeneralRuleContent'
import { GetCost } from 'src/api/ApiGeneral'
import OrderContent from './GeneralOrder'
import dayjs from 'dayjs'
import _ from 'lodash'

interface itemData {
  headers?: string[] // 表头
  instanceList?: string[][] // 列表数据
  totalCount?: string | number // 表格数据条数
}
interface ObjProps {
  productName?: string // 产品线名称
  productType?: string | number // 产品线type
  data?: itemData // 单产品线对应列表数据
  resourceNumber?: number // 资源数
  latestUpdated?: string // 计算时间
  rule?: string // 检查指标
}

const Cost: FC = () => {
  const [showSetting, setSetting] = useState(undefined) // 指标设置Dialog
  const [productType, setType] = useState(null)
  const [currentProduct, setCurrent] = useState<ObjProps>() // 当前操作的产品信息
  const [currentRule, setRule] = useState(null) // 当前指标详情
  const [tabConfig, setTabConfig] = useState(null) // 成本优化表格
  const [costData, setCostData] = useState([])
  const [showHeader] = useState(false)
  const rule = useRef(null) // 指标设置组件ref
  const dialogRef = useRef(null)
  // const order = useRef(null) // 订阅数据

  const getCost = async () => {
    const costRes = await GetCost()
    setCostData(costRes?.data?.costOptList || [])
  }
  useEffect(() => {
    getCost()
  }, [])

  // 生成表头
  const generateTabHeader = chosedItem => {
    const {
      data: { headers = [] },
      productName
    } = chosedItem || {}
    const tabHeader = {}
    headers.forEach((item, index) => {
      _.set(tabHeader, [`${productName}${String(index)}`], item)
    })
    // _.set(tabHeader, 'action', { title: settingHeaders, width: 50 })
    return tabHeader
  }
  // 生成表格数据
  const generateTabdata = chosedItem => {
    const {
      data: { totalCount, instanceList },
      productName
    } = chosedItem || {}
    const data =
      totalCount > 0
        ? instanceList.map(el => {
            const dealObj = {}
            el.forEach((item, index) => {
              _.set(dealObj, `${productName}${String(index)}`, item)
            })
            return dealObj
          })
        : []
    return data
  }
  useEffect(() => {
    if (costData && costData.length > 0) {
      const newProdouctType = productType !== null ? productType : costData[0]?.productType
      if (newProdouctType !== null) {
        setType(newProdouctType)
        const chosed = costData.find(i => i.productType === newProdouctType)
        setCurrent(chosed)
        const items = {
          scheme: generateTabHeader(chosed),
          data: generateTabdata(chosed)
        }
        setTabConfig(items)
      }
    }
  }, [costData])

  useEffect(() => {
    if (productType !== null) {
      setType(productType)
      const chosed = costData.find(i => i.productType === productType)
      setCurrent(chosed)
      const items = {
        scheme: generateTabHeader(chosed),
        data: generateTabdata(chosed)
      }
      setTabConfig(items)
    }
  }, [productType])

  // 获取指标信息
  const getCheckRules = async () => {
    const resRule = await QueryCheckRules({
      productType: productType
    })
    setRule(
      resRule?.data?.data?.map(item => ({
        ...item,
        days: item.days === 0 ? '' : item.days
      }))
    )
  }

  // 创建 & 修改指标
  const createRule = async () => {
    const createParams = rule.current && rule.current.getCheckedRules()
    createParams.then(res => {
      if (res) {
        const params = res.map(i => ({
          itemId: i.itemId,
          days: Number(i.days),
          threshold: Number(i.threshold)
        }))
        dialogRef?.current?.showLoading()
        CreateCheckRules({
          checkRules: params,
          productType: currentProduct?.productType
        })
          .then(res => {
            setSetting(false)
            dialogRef?.current?.hideLoading()
            if (res?.code === 'Success') {
              dialogRef?.current?.close()
              getCost()
              Message.success(res?.message)
            } else {
              Message.error(res?.message)
            }
          })
          .catch(err => {
            setSetting(false)
            throw err
          })
      }
    })
  }
  // 导出数据
  const dealExport = async () => {
    const exportParams = {
      productType: productType,
      pageIndex: 1,
      pageSize: null
    }
    const { data: instanceData = {} } = await GetCostOptInstances(exportParams)
    if (!instanceData?.data?.length) Message.warning(_$('实例数据为空'))
    else {
      handleExportForm(instanceData?.data)
    }
  }

  // 导出数据
  const handleExportForm = data => {
    const { productName, productType } = currentProduct
    //列标题，逗号隔开，每一个逗号就是隔开一个单元格
    let str = ''
    costData.forEach(v => {
      if (v.productType === productType) {
        str += v?.data?.headers.join(',')
        str += '\n'
      }
    })
    //增加\t为了不让表格显示科学计数法或者其他格式
    for (let i = 0; i < data.length; i++) {
      str += `${data[i] + '\t'}`
      str += '\n'
    }
    //encodeURIComponent解决中文乱码
    const uri = 'data:text/csv;charset=utf-8,\ufeff' + encodeURIComponent(str)
    //通过创建a标签实现
    const link = document.createElement('a')
    link.href = uri
    //对下载的文件命名
    const date = new Date()
    const dateNow = dayjs(date).format('YYYY-MM-DD hh-mm-ss')
    link.download = `${productName}_${dateNow}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // 产品类型下拉
  const productTypeSelect = (
    <Select
      onChangeValue={value => {
        setType(value)
      }}
      value={productType}
    >
      {[].concat(costData).map((i, index) => (
        <Option key={i?.productType + index} value={i?.productType}>
          {i?.productName}
        </Option>
      ))}
    </Select>
  )
  // 重置表头
  const checkedHeader = headers => {
    if (!currentProduct) return
    const { productName } = currentProduct
    const tabHeader = {}
    headers.forEach((item, index) => {
      if (item.checked === true) {
        _.set(tabHeader, [`${productName}${String(index)}`], item.title)
      }
    })
    // _.set(tabHeader, 'action', { title: settingHeaders, width: 50 })
    setTabConfig(state => ({
      ...state,
      scheme: tabHeader
    }))
  }

  return (
    <Card
      className="general-cost"
      slotTitle={
        <Tooltip
          content={_$('检查过去14天资源使用情况，当资源满足所有检查指标设置时，则计入待优化列表')}
          position="right"
        >
          <span style={{ fontWeight: 700, cursor: 'pointer' }}>
            {_$('成本优化')}
            <i style={{ marginLeft: '8px' }} className="icon icon-kpc-question1"></i>
          </span>
        </Tooltip>
      }
      slotExtra={<OrderContent />}
      style={{ marginTop: '20px' }}
    >
      {productTypeSelect}
      <Button
        style={{ margin: '0px 8px' }}
        type="secondary"
        onClick={() => {
          getCheckRules()
          setSetting(true)
        }}
      >
        {_$('指标设置')}
      </Button>
      <Button onClick={dealExport}>{_$('导出')}</Button>
      <div className="indicator-detail" style={{ position: 'relative' }}>
        <Row className="current-detail">
          <Col span={8}>
            {_$('资源数量')}：{currentProduct?.resourceNumber}
          </Col>
          <Col span={8}>
            {_$('计算时间')}：{currentProduct?.latestUpdated}
          </Col>
        </Row>
        <div className="current-indicator">
          <span>{_$('检查指标')}：</span>
          <div className="target-content">
            <span className="target-content-item">{currentProduct?.rule}</span>
          </div>
        </div>
        <TablePage
          tableProps={{
            type: 'border',
            checkType: 'none',
            ...tabConfig
          }}
        />
        <CoseHeaders
          changeHeader={checkedHeader}
          visible={showHeader}
          headers={currentProduct?.data?.headers || []}
        />
      </div>
      {/* 指标设置 */}
      <Dialog
        width={680}
        value={showSetting}
        ref={dialogRef}
        onChangeValue={show => {
          setSetting(show)
        }}
        ok={createRule}
        title={_$('指标设置')}
      >
        <RuleContent ref={rule} currentRule={currentRule} />
      </Dialog>
    </Card>
  )
}

export default Cost
